/**
 * WorkspaceInvitationsList Component - Invitation Management List
 *
 * Displays paginated list of workspace invitations with actions.
 *
 * @module components/workspace/WorkspaceInvitationsList
 */

import { useState, memo, useCallback } from 'react';
import {
  Stack,
  Group,
  Text,
  ActionIcon,
  Avatar,
  Tooltip,
  Select,
} from '@mantine/core';
import {
  IconRefresh,
  IconTrash,
  IconMail,
  IconClock,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { PaginatedList } from '../../ui/PaginatedList';
import { SearchAndFilters } from '../../ui/SearchAndFilters';
import { FilterPill } from '../../ui/FilterPill';
import { AppScrollArea } from '@/components/ui/AppScrollArea';
import { EmptyState } from '@/components/common/EmptyState';
import { ConfirmationModal } from '@/components/common/ConfirmationModal';
import { useLocalizedDate } from '../../../hooks/useLocalizedDate';
import { getAvatarColor, getEmailInitials } from '@/constants/roles';
import {
  MS_PER_DAY,
  ICON_SIZE_XS,
  ICON_SIZE_SM,
  ICON_SIZE_EMPTY_STATE,
  MEMBER_DETAILS_GAP,
  FONT_WEIGHT_MEDIUM,
  TEAM_PANEL_LIST_MAX_HEIGHT,
} from '@/constants/ui';
import { canManageMembers } from '../../../types/workspace';
import type {
  WorkspaceInvitation,
  InvitationStatus,
  WorkspaceRole,
} from '../../../types/workspace';
import styles from './WorkspaceInvitationsList.module.css';

interface WorkspaceInvitationsListProps {
  /** List of invitations */
  invitations: WorkspaceInvitation[];
  /** Whether there are more invitations to load */
  hasNextPage: boolean;
  /** Whether invitations are loading */
  isLoading: boolean;
  /** Whether the invitations list has completed at least one fetch */
  hasLoaded: boolean;
  /** Search value */
  search: string;
  /** Status filter */
  statusFilter: InvitationStatus | null;
  /** Role filter */
  roleFilter: WorkspaceRole | null;
  /** Current user's role in workspace */
  currentUserRole: WorkspaceRole;
  /** Callback when search changes */
  onSearchChange: (search: string) => void;
  /** Callback when status filter changes */
  onStatusFilterChange: (status: InvitationStatus | null) => void;
  /** Callback when role filter changes */
  onRoleFilterChange: (role: WorkspaceRole | null) => void;
  /** Callback to load more invitations */
  onLoadMore: () => void;
  /** Callback to resend invitation */
  onResend: (invitationId: string) => Promise<boolean>;
  /** Callback to cancel invitation */
  onCancel: (invitationId: string) => Promise<boolean>;
  /** Error message */
  error?: string | null;
}

const STATUS_OPTIONS: InvitationStatus[] = ['Pending', 'Expired'];
const ROLE_OPTIONS: WorkspaceRole[] = ['Admin', 'Editor', 'Viewer'];

/** Calculate days remaining until expiry as a short label (e.g. "5d") */
const getDaysUntilExpiry = (expiresAt: string): string => {
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffMs = expiry.getTime() - now.getTime();
  const diffDays = Math.max(0, Math.ceil(diffMs / MS_PER_DAY));
  return `${diffDays}d`;
};

export const WorkspaceInvitationsList = memo(function WorkspaceInvitationsList({
  invitations,
  hasNextPage,
  isLoading,
  hasLoaded,
  search,
  statusFilter,
  roleFilter,
  currentUserRole,
  onSearchChange,
  onStatusFilterChange,
  onRoleFilterChange,
  onLoadMore,
  onResend,
  onCancel,
  error,
}: WorkspaceInvitationsListProps) {
  const { t } = useTranslation();
  const { formatRelativeTime } = useLocalizedDate();
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [invitationToCancel, setInvitationToCancel] =
    useState<WorkspaceInvitation | null>(null);

  const canManage = canManageMembers(currentUserRole);
  const hasActiveFilters = statusFilter !== null || roleFilter !== null;

  const handleFilterToggle = useCallback(() => {
    setFiltersExpanded((prev) => !prev);
  }, []);

  const handleStatusChange = useCallback(
    (value: string | null) => {
      onStatusFilterChange(
        value === 'all' ? null : (value as InvitationStatus)
      );
    },
    [onStatusFilterChange]
  );

  const handleRoleFilterChange = useCallback(
    (value: string | null) => {
      onRoleFilterChange(
        value === 'all' ? null : (value as WorkspaceRole | null)
      );
    },
    [onRoleFilterChange]
  );

  const clearStatusFilter = useCallback(() => {
    onStatusFilterChange(null);
  }, [onStatusFilterChange]);

  const clearRoleFilter = useCallback(() => {
    onRoleFilterChange(null);
  }, [onRoleFilterChange]);

  const getRoleLabel = useCallback(
    (role: WorkspaceRole) => t(`workspace.roles.${role.toLowerCase()}`),
    [t]
  );

  const getStatusLabel = useCallback(
    (status: InvitationStatus) =>
      t(`workspace.invitationStatus.${status.toLowerCase()}`),
    [t]
  );

  const renderInvitation = useCallback(
    (invitation: WorkspaceInvitation, _index: number, _isFocused: boolean) => {
      const isPending = invitation.status === 'Pending';

      const handleResend = async () => {
        await onResend(invitation.id);
      };

      const handleCancel = () => {
        setInvitationToCancel(invitation);
      };

      return (
        <Group
          className={styles.invitationRow}
          justify="space-between"
          wrap="nowrap"
        >
          <Group gap="sm" wrap="nowrap" className={styles.invitationInfo}>
            <Avatar
              radius="xl"
              size="md"
              color={getAvatarColor(invitation.email)}
            >
              {getEmailInitials(invitation.email)}
            </Avatar>

            <Stack
              gap={MEMBER_DETAILS_GAP}
              className={styles.invitationDetails}
            >
              <Text fw={FONT_WEIGHT_MEDIUM} truncate>
                {invitation.email}
              </Text>
              <Group gap={6} wrap="nowrap" className={styles.metaLine}>
                <Text size="xs" c="dimmed">
                  {t(`workspace.roles.${invitation.role.toLowerCase()}`)}
                </Text>
                <span className={styles.dotSeparator} />
                <Text size="xs" c="dimmed">
                  {t('workspace.invitations.sentAt', {
                    date: formatRelativeTime(new Date(invitation.sentAt)),
                  })}
                </Text>
              </Group>
            </Stack>
          </Group>

          <Group gap="sm" wrap="nowrap">
            {isPending && invitation.expiresAt && (
              <Group gap={4} wrap="nowrap" className={styles.expiryBadge}>
                <IconClock size={ICON_SIZE_XS} />
                <Text size="xs" c="dimmed">
                  {getDaysUntilExpiry(invitation.expiresAt)}
                </Text>
              </Group>
            )}

            {canManage && isPending && (
              <Group gap={4} wrap="nowrap" className={styles.actionButtons}>
                <Tooltip label={t('workspace.invitations.resend')}>
                  <ActionIcon variant="subtle" size="sm" onClick={handleResend}>
                    <IconRefresh size={ICON_SIZE_SM} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label={t('workspace.invitations.cancel')}>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="sm"
                    onClick={handleCancel}
                  >
                    <IconTrash size={ICON_SIZE_SM} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            )}
          </Group>
        </Group>
      );
    },
    [canManage, onResend, formatRelativeTime, t]
  );

  const keyExtractor = useCallback(
    (invitation: WorkspaceInvitation, _index: number) => invitation.id,
    []
  );

  const handleConfirmCancel = useCallback(async () => {
    if (!invitationToCancel) return;
    await onCancel(invitationToCancel.id);
    setInvitationToCancel(null);
  }, [invitationToCancel, onCancel]);

  const handleCloseCancel = useCallback(() => setInvitationToCancel(null), []);

  return (
    <Stack gap="md">
      <SearchAndFilters variant="compact">
        <SearchAndFilters.Header>
          <SearchAndFilters.SearchBar
            placeholder={t('workspace.invitations.searchPlaceholder')}
            value={search}
            onChange={onSearchChange}
          />
          <SearchAndFilters.FilterToggle
            expanded={filtersExpanded}
            hasActiveFilters={hasActiveFilters}
            onClick={handleFilterToggle}
          />
        </SearchAndFilters.Header>

        <SearchAndFilters.Section
          hasContent={hasActiveFilters || filtersExpanded}
        >
          <SearchAndFilters.Pills show={!filtersExpanded && hasActiveFilters}>
            {roleFilter && (
              <FilterPill
                label={t('workspace.filters.role')}
                value={getRoleLabel(roleFilter)}
                onRemove={clearRoleFilter}
                ariaLabel={t('workspace.filters.clearRole')}
              />
            )}
            {statusFilter && (
              <FilterPill
                label={t('workspace.filters.status')}
                value={getStatusLabel(statusFilter)}
                onRemove={clearStatusFilter}
                ariaLabel={t('workspace.filters.clearStatus')}
              />
            )}
          </SearchAndFilters.Pills>
          <SearchAndFilters.FiltersPanel expanded={filtersExpanded}>
            <Select
              label={t('workspace.filters.role')}
              data={[
                {
                  value: 'all',
                  label: t('workspace.filters.allRoles'),
                },
                ...ROLE_OPTIONS.map((role) => ({
                  value: role,
                  label: getRoleLabel(role),
                })),
              ]}
              value={roleFilter ?? 'all'}
              onChange={handleRoleFilterChange}
              size="sm"
              comboboxProps={{ withinPortal: true }}
            />
            <Select
              label={t('workspace.filters.status')}
              data={[
                {
                  value: 'all',
                  label: t('workspace.invitations.allStatuses'),
                },
                ...STATUS_OPTIONS.map((status) => ({
                  value: status,
                  label: getStatusLabel(status),
                })),
              ]}
              value={statusFilter ?? 'all'}
              onChange={handleStatusChange}
              size="sm"
              comboboxProps={{ withinPortal: true }}
            />
          </SearchAndFilters.FiltersPanel>
        </SearchAndFilters.Section>
      </SearchAndFilters>

      <AppScrollArea maxHeight={TEAM_PANEL_LIST_MAX_HEIGHT} clampWidth>
        <PaginatedList
          data={invitations}
          renderItem={renderInvitation}
          keyExtractor={keyExtractor}
          hasNextPage={hasNextPage}
          isLoading={isLoading}
          hasLoaded={hasLoaded}
          onLoadMore={onLoadMore}
          error={error}
          stateRenderers={{
            empty: () => (
              <EmptyState
                title={t('workspace.invitations.emptyTitle')}
                description={t('workspace.invitations.emptyHint')}
                icon={<IconMail size={ICON_SIZE_EMPTY_STATE} />}
                size="md"
              />
            ),
          }}
          styling={{
            gap: 'xs',
          }}
        />
      </AppScrollArea>

      <ConfirmationModal
        opened={invitationToCancel !== null}
        onClose={handleCloseCancel}
        onConfirm={handleConfirmCancel}
        variant="danger"
        title={t('workspace.invitations.cancelTitle')}
        message={t('workspace.invitations.cancelConfirm', {
          email: invitationToCancel?.email ?? '',
        })}
        confirmLabel={t('workspace.invitations.cancelConfirmAction')}
      />
    </Stack>
  );
});
